import { defineStore } from 'pinia'
import { computed, ref, toValue } from 'vue'
import { enrollmentExperiment, FeatureFlag } from '../Integrations/Experiment'
import { Action, Actor, enrollmentTracking } from '../Integrations/Tracking'
import {
    Address,
    BusinessDetails,
    CompanyDetails,
    ContactDetails,
    EligibilitySubmission,
    EligibilitySubmissionAssessment,
    EligibilitySubmissionData,
    EligibilitySubmissionDataFromQuery,
    PersonalDetails,
    Question,
    SimulationResult,
    StatusDashboardData,
    TechniqueAssessment,
    TechniqueAssessmentResponse,
} from '../global'
import { client, injectToken } from '../http'
import { i18n } from '../i18n'
import { Audience, handleError, toAtomString } from '../utils'

export const useEligibilitySubmissionStore = defineStore('eligibilitySubmission', () => {
    const { t, locale } = i18n.global

    const eligibilitySubmission = ref<EligibilitySubmission>({} as EligibilitySubmission)
    const statusDashboardAuthorizationToken = ref<string>()
    const error = ref<string>()
    const loading = ref<boolean>(false)
    const hasStateFilled = computed<boolean>(
        () => !!Object.keys(eligibilitySubmission.value).length
    )
    const isMissingPersonalDetails = computed<boolean>(
        () =>
            eligibilitySubmission.value.campaignAudience === Audience.Homeowners &&
            [
                eligibilitySubmission.value.firstName,
                eligibilitySubmission.value.lastName,
                eligibilitySubmission.value.phoneNumber,
                eligibilitySubmission.value.emailAddress,
                eligibilitySubmission.value.address?.street,
                eligibilitySubmission.value.address?.number,
                eligibilitySubmission.value.address?.city,
                eligibilitySubmission.value.address?.postalCode,
            ].some((value) => value === undefined || value === null || value === '')
    )

    const isMissingBusinessDetails = computed<boolean>(
        () =>
            eligibilitySubmission.value.campaignAudience === Audience.BusinessOwners &&
            [
                eligibilitySubmission.value.companyName,
                eligibilitySubmission.value.vatNumber,
                eligibilitySubmission.value.firstName,
                eligibilitySubmission.value.lastName,
                eligibilitySubmission.value.phoneNumber,
                eligibilitySubmission.value.emailAddress,
                eligibilitySubmission.value.address?.street,
                eligibilitySubmission.value.address?.number,
                eligibilitySubmission.value.address?.city,
                eligibilitySubmission.value.address?.postalCode,
            ].some((value) => value === undefined || value === null || value === '')
    )

    const campaignSimulation = computed<boolean>(
        () => eligibilitySubmission.value.advisors?.simulation !== undefined
    )

    const campaignSwapPIScreen = computed<boolean>(() => {
        if (!eligibilitySubmission.value.campaignSwapPIScreen) {
            return false
        }

        return enrollmentExperiment.active(FeatureFlag.SwapPIScreen)
    })

    const isApartment = computed<boolean>(() => !!eligibilitySubmission.value.assessment?.apartment)

    async function initializeSubmission(submission: EligibilitySubmission): Promise<void> {
        eligibilitySubmission.value = submission
        enrollmentTracking.identify(submission)
        await enrollmentExperiment.start(submission)
    }

    async function check(campaignId: string, ssoTokenKbc: string): Promise<void> {
        loading.value = true

        try {
            const { data } = await client.post<EligibilitySubmissionData | StatusDashboardData>(
                `/kbc/campaigns/${campaignId}/mobile-entry-point`,
                {
                    ssoTokenKbc,
                    privacyPolicyDisplayedAt: toAtomString(new Date()),
                    language: toValue(locale),
                }
            )

            if ('statusDashboardAuthorizationToken' in data) {
                window.location.replace(
                    `/dashboard/${data.statusDashboardAuthorizationToken}/entries?lang=${toValue(
                        locale
                    )}`
                )
                return
            }

            await initializeSubmission(data.eligibilitySubmission)
            injectToken(data.authorizationToken)
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function createFromEntry(entryToken: string, campaignId: string): Promise<void> {
        loading.value = true

        try {
            const { data } = await client.post<EligibilitySubmissionData>(
                `/eligibility-submissions/create-from-entry/${entryToken}`,
                {
                    campaignId,
                }
            )
            await initializeSubmission(data.eligibilitySubmission)
            injectToken(data.authorizationToken)
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function createFromSubmission(
        submissionToken: string,
        submissionId: string
    ): Promise<void> {
        loading.value = true

        console.log(client.defaults.headers.common)

        try {
            const { data } = await client.post<EligibilitySubmissionData>(
                `/eligibility-submissions/create-from-submission/${submissionToken}`,
                {
                    submissionId,
                }
            )
            await initializeSubmission(data.eligibilitySubmission)
            injectToken(data.authorizationToken)
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function createFromCampaign(
        campaignId: string,
        eligibilitySubmissionData: Partial<EligibilitySubmissionDataFromQuery>
    ): Promise<void> {
        loading.value = true

        try {
            const { data } = await client.post<EligibilitySubmissionData>(
                `/campaigns/${campaignId}/eligibility-submission`,
                eligibilitySubmissionData
            )

            await initializeSubmission(data.eligibilitySubmission)
            injectToken(data.authorizationToken)
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function continueFromSubmission(token: string, submissionId: string): Promise<void> {
        loading.value = true

        try {
            injectToken(token)

            const { data } = await client.get<EligibilitySubmissionData>(
                `/eligibility-submissions/${submissionId}`
            )

            await initializeSubmission(data.eligibilitySubmission)
            enrollmentTracking.track(Actor.Homeowner, Action.ContinueSubmission)
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function updateContactDetails(data: ContactDetails): Promise<void> {
        if (!data.phoneNumber || !data.emailAddress) {
            error.value = t('Please fill out all required fields with valid values.')
            return Promise.reject()
        }

        loading.value = true

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/personal-information`,
                data
            )
            error.value = undefined
        } catch (e: any) {
            error.value = handleError(
                e,
                t('Please fill out all required fields with valid values.')
            )
        }

        loading.value = false
    }

    async function updateCompanyDetails(data: CompanyDetails): Promise<void> {
        if (!data.companyName || !data.vatNumber) {
            error.value = t('Please fill out all required fields with valid values.')
            return Promise.reject()
        }

        loading.value = true

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/personal-information`,
                data
            )
            error.value = undefined
        } catch (e: any) {
            error.value = handleError(
                e,
                t('Please fill out all required fields with valid values.')
            )
        }

        loading.value = false
    }

    async function updateAddress(data: Address): Promise<void> {
        loading.value = true

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/personal-information`,
                { constructionAddress: data }
            )
            eligibilitySubmission.value.address = data
            error.value = undefined
        } catch (e: any) {
            error.value = handleError(
                e,
                t('Please fill out all required fields with valid values.')
            )
        }

        loading.value = false
    }

    async function updateDetails(data: PersonalDetails | BusinessDetails): Promise<void> {
        loading.value = true

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/personal-information`,
                data
            )
            error.value = undefined

            eligibilitySubmission.value = {
                ...eligibilitySubmission.value,
                ...data,
                address: data.constructionAddress,
            }
        } catch (e: any) {
            error.value = handleError(
                e,
                t('Please fill out all required fields with valid values.')
            )
        }

        loading.value = false
    }

    async function answerQuestions(questions: Question[]): Promise<void> {
        loading.value = true

        const data = {
            data: questions.map(({ questionId, answer }) => ({
                questionId,
                answer: Array.isArray(answer) ? answer.join(',') : (answer ?? null),
            })),
        }

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/answer-questions`,
                data
            )
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function saveSimulationAnswers(answers: Record<string, number>): Promise<void> {
        const entries = Object.entries(answers)
        if (entries.length === 0) return

        loading.value = true

        const data = {
            data: entries.map(([questionId, value]) => ({
                questionId,
                answer: String(value),
            })),
        }

        try {
            await client.put(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/answer-questions`,
                data
            )
            error.value = undefined
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function getAssessment(): Promise<void> {
        loading.value = true

        try {
            const { data } = await client.get<{ data: EligibilitySubmissionAssessment }>(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/assessment`
            )
            error.value = undefined
            eligibilitySubmission.value.assessment = data.data
        } catch (e: any) {
            error.value = e.message
        }

        loading.value = false
    }

    async function finalize(techniqueIds: string[] = []): Promise<void> {
        loading.value = true

        const body = {
            termsAcceptedAt: eligibilitySubmission.value.termsAndConditionsAcceptedAt,
            privacyPolicyDisplayedAt: eligibilitySubmission.value.privacyPolicyDisplayedAt,
            selectedTechniques: techniqueIds,
        }

        try {
            const { data } = await client.post<StatusDashboardData>(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/finalize`,
                body
            )
            statusDashboardAuthorizationToken.value = data.statusDashboardAuthorizationToken
            error.value = undefined
        } catch (e: any) {
            error.value = handleError(e)
        }

        loading.value = false
    }

    async function getSimulation(
        selectedTechniques: string[] = [],
        overrides: Record<string, number> = {}
    ): Promise<SimulationResult[]> {
        loading.value = true
        error.value = undefined

        const queryParameter = new URLSearchParams(
            selectedTechniques.map((id) => ['selectedTechniques[]', id])
        )

        Object.entries(overrides).forEach(([key, value]) => {
            queryParameter.append(`overrides[${key}]`, String(value))
        })

        try {
            const { data } = await client.get<{ data: SimulationResult[] }>(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/simulation?${queryParameter.toString()}`
            )

            return data.data
        } catch (e) {
            error.value = handleError(e)
            return []
        } finally {
            loading.value = false
        }
    }

    async function statusForTechniques(
        selectedTechniques: string[] = []
    ): Promise<TechniqueAssessment[]> {
        loading.value = true

        const queryParameter = new URLSearchParams(
            selectedTechniques.map((id) => ['selectedTechniques[]', id])
        )

        try {
            const { data } = await client.get<TechniqueAssessmentResponse>(
                `/eligibility-submissions/${eligibilitySubmission.value.id}/techniques?${queryParameter.toString()}`
            )

            return data.data
        } catch (e: any) {
            error.value = handleError(e)
        } finally {
            loading.value = false
        }

        return []
    }

    function touchPrivacyPolicyDisplayedAt(): void {
        eligibilitySubmission.value.privacyPolicyDisplayedAt = toAtomString(new Date())
    }

    function touchTermsAndConditionsAcceptedAt(): void {
        eligibilitySubmission.value.termsAndConditionsAcceptedAt = toAtomString(new Date())
    }

    function setDisqualificationReason(
        reason: string,
        description: string,
        postalCodeEligible: boolean = true
    ): void {
        eligibilitySubmission.value.assessment = {
            qualified: false,
            disqualificationReason: reason,
            disqualificationDescription: description,
            postalCodeEligible,
        }
    }

    function redirectToSetle(): void {
        const {
            advisors: { setle },
        } = eligibilitySubmission.value

        if (!setle?.url) {
            return
        }
        enrollmentTracking.track(Actor.Homeowner, Action.StartSubmission)
        enrollmentTracking.track(Actor.Homeowner, Action.RedirectToSetle)
        window.location.assign(setle?.url)
    }

    return {
        eligibilitySubmission,
        statusDashboardAuthorizationToken,
        error,
        loading,
        hasStateFilled,
        isMissingPersonalDetails,
        isMissingBusinessDetails,
        campaignSimulation,
        campaignSwapPIScreen,
        isApartment,
        check,
        createFromEntry,
        createFromSubmission,
        createFromCampaign,
        updateContactDetails,
        updateCompanyDetails,
        updateDetails,
        updateAddress,
        answerQuestions,
        saveSimulationAnswers,
        getAssessment,
        finalize,
        getSimulation,
        statusForTechniques,
        touchPrivacyPolicyDisplayedAt,
        touchTermsAndConditionsAcceptedAt,
        continueFromSubmission,
        redirectToSetle,
        setDisqualificationReason,
    }
})
